import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowLeft, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const TEAM_SIZES = [
  '1–10 people',
  '11–50 people',
  '51–200 people',
  '201–1,000 people',
  '1,000+ people',
];

const USE_CASES = [
  'Executive dashboards',
  'Financial reporting & FP&A',
  'Sales & marketing analytics',
  'Operations / supply chain',
  'Product analytics',
  'AI-native self-service BI',
  'Replacing an existing BI tool',
  'Something else',
];

export default function RequestDemoPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    job_title: '',
    team_size: '',
    use_case: '',
    preferred_time: '',
    message: '',
  });

  const setField = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Please fill in your name and work email.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/marketing/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || 'Something went wrong. Please try again.');
      }
      setDone(true);
    } catch (err) {
      toast.error(err.message || 'Failed to submit — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center p-6" data-testid="request-demo-success">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mb-3">Thanks — we&apos;ll be in touch.</h1>
          <p className="text-slate-400 leading-relaxed">
            A member of the INZARA team will reach out within one business day to schedule your personalized demo at a time that works for you.
          </p>
          <p className="text-slate-500 text-sm mt-4">
            Want to try INZARA in the meantime? <button onClick={() => navigate('/register')} className="text-cyan-400 hover:text-cyan-300 underline">Start a free trial</button> — no credit card required.
          </p>
          <button
            onClick={() => navigate('/welcome')}
            className="mt-8 text-slate-500 hover:text-slate-300 text-sm transition-colors"
            data-testid="back-to-home-btn"
          >
            ← Back to home
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="request-demo-page">
      {/* subtle background sheen */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-cyan-500/[0.04] blur-[120px] rounded-full" />
      </div>

      <header className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8 py-6 flex items-center justify-between">
        <button
          onClick={() => navigate('/welcome')}
          className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <div className="text-lg font-display font-semibold tracking-tight">INZARA</div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8 py-12 md:py-16 grid lg:grid-cols-5 gap-12 items-start">
        {/* Left: pitch */}
        <div className="lg:col-span-2 lg:sticky lg:top-24">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.03] border border-white/[0.08] text-xs text-cyan-300 mb-6">
            <Sparkles className="w-3 h-3" />
            Personalized 30-min demo
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1] mb-6">
            See how INZARA fits your data stack.
          </h1>
          <p className="text-slate-400 text-lg leading-relaxed mb-8">
            Tell us a bit about your team and we&apos;ll tailor a walkthrough to your actual use cases — connectors, dashboards, AI copilot, governance, all of it.
          </p>
          <ul className="space-y-3">
            {[
              'Live demo with an INZARA solutions engineer',
              'Custom to your industry & data warehouse',
              'Full Q&A — bring your hardest analytics questions',
              'Optional: sandbox with your own data afterward',
            ].map(li => (
              <li key={li} className="flex items-start gap-3 text-sm text-slate-300">
                <Check className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
                {li}
              </li>
            ))}
          </ul>
        </div>

        {/* Right: form */}
        <form
          onSubmit={submit}
          className="lg:col-span-3 bg-white/[0.02] border border-white/[0.08] rounded-2xl p-8 backdrop-blur-sm"
          data-testid="demo-request-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Your name *" required>
              <input
                required
                data-testid="input-name"
                value={form.name}
                onChange={setField('name')}
                className={inputCls}
                placeholder="Jane Smith"
              />
            </Field>
            <Field label="Work email *" required>
              <input
                required
                type="email"
                data-testid="input-email"
                value={form.email}
                onChange={setField('email')}
                className={inputCls}
                placeholder="jane@company.com"
              />
            </Field>
            <Field label="Company">
              <input
                data-testid="input-company"
                value={form.company}
                onChange={setField('company')}
                className={inputCls}
                placeholder="Acme Corp"
              />
            </Field>
            <Field label="Job title">
              <input
                data-testid="input-job-title"
                value={form.job_title}
                onChange={setField('job_title')}
                className={inputCls}
                placeholder="Head of Analytics"
              />
            </Field>
            <Field label="Team size">
              <select
                data-testid="input-team-size"
                value={form.team_size}
                onChange={setField('team_size')}
                className={inputCls}
              >
                <option value="">Select…</option>
                {TEAM_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Primary use case">
              <select
                data-testid="input-use-case"
                value={form.use_case}
                onChange={setField('use_case')}
                className={inputCls}
              >
                <option value="">Select…</option>
                {USE_CASES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <div className="md:col-span-2">
              <Field label="Preferred day/time (optional)">
                <input
                  data-testid="input-preferred-time"
                  value={form.preferred_time}
                  onChange={setField('preferred_time')}
                  className={inputCls}
                  placeholder="e.g. Tuesday afternoon, PT"
                />
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field label="Anything else we should know?">
                <textarea
                  data-testid="input-message"
                  value={form.message}
                  onChange={setField('message')}
                  rows={4}
                  className={inputCls}
                  placeholder="Tell us about your data sources, existing BI stack, or specific goals for the demo."
                />
              </Field>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="submit-demo-request-btn"
            className="mt-8 w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white text-black font-semibold text-sm hover:bg-slate-200 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Submitting…
              </>
            ) : 'Request my demo'}
          </button>
          <p className="text-xs text-slate-500 mt-4 text-center">
            We&apos;ll only use this to schedule your demo. No spam, ever.
          </p>
        </form>
      </main>

      {/* SEO / value content — substantive, keyword-rich copy so the demo page
          carries real information for search engines and prospects alike. */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 pb-24 space-y-12">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">What is INZARA?</h2>
          <p className="text-slate-400 leading-relaxed">
            INZARA is an AI-native business intelligence and analytics platform that lets teams ask
            questions of their data in plain English and get governed, trustworthy answers in seconds.
            Instead of waiting on analysts to build every report, business users explore live data,
            generate dashboards and reports automatically, and drill into the “why” behind a number
            with an AI copilot that reasons over a certified semantic model. INZARA connects directly
            to your data warehouse — Snowflake, BigQuery, Redshift, Databricks, ClickHouse, Postgres,
            DuckDB and more — so numbers stay accurate, secure, and consistent across every team.
          </p>
        </div>

        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">Why teams book an INZARA demo</h2>
          <p className="text-slate-400 leading-relaxed mb-6">
            A live demo is the fastest way to see how INZARA replaces slow, fragmented reporting with
            self-service analytics your whole organization can trust. In 30 minutes, an INZARA
            solutions engineer walks through the platform using examples from your industry and shows
            how quickly you can go from a raw warehouse table to an executive-ready dashboard.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              ['AI Explore', 'Ask questions in natural language and get charts, tables, and narrative insights backed by a governed semantic layer — no SQL required.'],
              ['Dashboards & reports', 'Auto-generate interactive dashboards and pixel-perfect reports, then schedule and share them across email, Slack, and Microsoft Teams.'],
              ['Root-cause investigations', 'Let the AI copilot investigate why a metric moved, rank the drivers, and deliver a confidence-scored verdict you can act on.'],
              ['Governance you can trust', 'Certify metrics once and reuse them everywhere, with row-level security, data lineage, and impact analysis built in.'],
            ].map(([t, d]) => (
              <div key={t} className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-5">
                <h3 className="font-semibold text-white mb-1.5">{t}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">What to expect on the call</h2>
          <p className="text-slate-400 leading-relaxed">
            We keep demos practical and specific to your stack. Come with your hardest analytics
            questions — how you report revenue, monitor KPIs, forecast demand, or replace an existing
            BI tool such as Tableau, Power BI, or Looker. We’ll show how INZARA models your data,
            enforces governance, and puts AI-driven exploration in the hands of every stakeholder.
            After the call, we can spin up a private sandbox connected to your own data so your team
            can evaluate INZARA hands-on, at your own pace.
          </p>
        </div>

        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">Frequently asked questions</h2>
          <div className="space-y-5">
            {[
              ['How long is the demo?', 'About 30 minutes, including plenty of time for questions. We tailor the agenda to your team’s goals.'],
              ['Do I need to prepare anything?', 'No preparation required. It helps to know which data sources and use cases matter most, but we can start from scratch.'],
              ['Can INZARA connect to my data warehouse?', 'Yes. INZARA has native connectors for Snowflake, BigQuery, Redshift, Databricks, ClickHouse, PostgreSQL, MySQL, DuckDB, and more, with query push-down for speed on large tables.'],
              ['Is my data secure?', 'INZARA queries your warehouse directly with role-based access control and row-level security. You keep control of your data and permissions.'],
              ['Can I try it before buying?', 'Absolutely — you can start a free trial with no credit card, and we can provision a sandbox with your own data after the demo.'],
            ].map(([q, a]) => (
              <div key={q}>
                <h3 className="font-semibold text-white mb-1">{q}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full px-4 py-2.5 bg-black/40 border border-white/[0.08] rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50 focus:ring-1 focus:ring-cyan-400/30 transition-colors";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
